import React, { useEffect, useState } from 'react';
import {
	Button,
	Checkbox,
	FormControl,
	FormErrorMessage,
	FormLabel,
	HStack,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
	Textarea,
	Select,
	Spinner,
	Stack,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	useToast,
	useDisclosure,
} from '@chakra-ui/react';
import axios from 'axios';
import config from '../config';
import globalStore from '../store';
import { DataSelector } from '@repo/alictus-common/types/segment';
import { DataSource } from '@repo/alictus-common/enums/data_source';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import dataSelectorSchema from '@repo/alictus-common/validation/data_selector_schema';

import { Field, Form, Formik } from 'formik';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';

type NestedMenuItem = {
	[key: string]: NestedMenuItem | null;
};

export function SelectorModal({
	isOpen,
	onClose,
	selectorInstance,
	isAdminOrProduct,
}: {
	isOpen: boolean;
	onClose: () => void;
	selectorInstance?: DataSelector;
	isAdminOrProduct?: boolean;
}) {
	const createEmptyDataSelector = (): DataSelector => {
		return {
			id: 0,
			creationDate: 0,
			isDeleted: false,
			isOverWritten: false,
			lastUpdated: 0,
			uid: '',
			userId: 0,
			name: '',
			description: '',
			dataSource: DataSource.Playfab,
			fieldName: '',
			isDate: false,
			isJsonQuery: false,
			isSqlQuery: false,
			isMock: isMockCreator,
			isSticky: false,
		};
	};

	let globalData = globalStore();
	const toast = useToast();
	const isMockCreator = globalData.user?.roles.includes('MOCK_CREATOR') || false;
	const [dataSelector, setDataSelector] = useState<DataSelector>(createEmptyDataSelector());
	const [showSubmitButton, setShowSubmitButton] = useState(true);
	const [playfabUserId, setPlayfabUserId] = useState('');
	const [playfabUserData, setPlayfabUserData] = useState<NestedMenuItem>({});
	const [isFetching, setIsFetching] = useState(false);
	const [isMock, setIsMock] = useState(isMockCreator);
	const [isSticky, setIsSticky] = useState(false);
	const [selectedPath, setSelectedPath] = useState<string>('');
	const { onClose: onCloseModal } = useDisclosure();
	const [warningMessage, setWarningMessage] = useState('');
	const [selectorType, setSelectorType] = useState('');
	const [previousSelectorType, setPreviousSelectorType] = useState<string | null>(null);

	useEffect(() => {
		if (selectorInstance) {
			console.log('Selector instance', selectorInstance);
			setDataSelector(selectorInstance);
			setSelectorType(
				selectorInstance.isDate ? 'isDate' : selectorInstance.isJsonQuery ? 'isJsonQuery' : selectorInstance.isSqlQuery ? 'isSqlQuery' : '',
			);
			setIsMock(selectorInstance.isMock);
			setIsSticky(selectorInstance.isSticky || false);
			setSelectedPath(selectorInstance.fieldName);
		} else {
			// If creating a new selector, ensure synchronization
			setDataSelector(createEmptyDataSelector());
			setSelectorType(''); // Reset selectorType
			setIsMock(isMockCreator);
			setIsSticky(false);
		}
	}, [selectorInstance, isMockCreator]);

	useEffect(() => {
		// Reset fieldName only when transitioning between groups
		if (!selectorInstance) {
			const group1 = ['isJsonQuery', 'isSqlQuery'];
			const group2 = ['isDate', ''];

			const isGroup1 = group1.includes(selectorType);
			const wasGroup1 = group1.includes(previousSelectorType || '');
			const isGroup2 = group2.includes(selectorType);
			const wasGroup2 = group2.includes(previousSelectorType || '');

			if ((isGroup1 && !wasGroup1) || (isGroup2 && !wasGroup2)) {
				// Transition between groups, reset fieldName and selectedPath
				updateDataSelector({ fieldName: '' });
				setSelectedPath('');
			}

			// Update previousSelectorType
			setPreviousSelectorType(selectorType);
		}
	}, [selectorType]);

	const resetForm = () => {
		setDataSelector(createEmptyDataSelector());
	};

	function updateDataSelector(dataSelectorField: Partial<DataSelector>) {
		setDataSelector({ ...dataSelector, ...dataSelectorField });
		console.log('Updated data selector', dataSelector);
	}

	function handleClose() {
		setShowSubmitButton(true);
		resetForm();
		onClose();
	}

	// Recursive rendering of menu items
	const renderMenuItems = (
		menuData: NestedMenuItem,
		currentPath: string = '',
		onCloseModal: () => void,
		setFieldValue: (path: string) => void,
	) => {
		if (!menuData || typeof menuData !== 'object') return null;

		return Object.entries(menuData).map(([key, value]) => {
			const newPath = currentPath ? `${currentPath}.${key}` : key;

			if (value && typeof value === 'object') {
				// Nested menu for items with children
				return (
					<Menu key={key} placement="right-start" isLazy>
						<MenuButton paddingLeft={'auto'} as={MenuItem} _hover={{ background: 'orange.700' }} closeOnSelect={false}>
							{key} <ChevronRightIcon ml="2" />
						</MenuButton>
						<MenuList paddingLeft={'auto'} maxHeight="400px" overflowY="auto">
							{renderMenuItems(value, newPath, onCloseModal, setFieldValue)}
						</MenuList>
					</Menu>
				);
			}

			// Leaf node menu item
			return (
				<MenuItem
					paddingLeft={'auto'}
					_hover={{ background: 'yellow.400' }}
					key={key}
					onClick={() => {
						setSelectedPath(newPath);
						updateDataSelector({ fieldName: newPath });
						setFieldValue(newPath);
						onCloseModal();
					}}
				>
					{key}
				</MenuItem>
			);
		});
	};

	const handleCreateSelector = async (event?: React.FormEvent) => {
		if (event) event.preventDefault();
		try {
			setShowSubmitButton(false);
			let url = config.CLOUDFLARE_LOCAL_ENDPOINT + '/game/' + globalData.gameId + '/selector';
			await axios.post(url, dataSelector, { headers: { Authorization: 'Bearer ' + globalData.JWTToken } }).then((response) => {
				console.log(response);
				toast({
					title: 'Selector Created Successfully',
					description: 'The selector has been created successfully.',
					status: 'success',
					duration: 5000,
					isClosable: true,
					position: 'top',
				});
				handleClose();
			});
		} catch (error: any) {
			console.error(error);
			toast({
				title: 'Error Creating Selector',
				description: error.response?.data,
				status: 'error',
				duration: 10000,
				isClosable: true,
				position: 'top',
			});
		} finally {
			setShowSubmitButton(true);
		}
	};

	const handleUpdateSelector = async (selectorId: number) => {
		try {
			setShowSubmitButton(false);
			const updatedDataSelector = { ...dataSelector, isMock, isSticky };
			console.log('Updating selector with data:', updatedDataSelector);
			let url = config.CLOUDFLARE_LOCAL_ENDPOINT + '/game/' + globalData.gameId + '/selector/' + selectorId;
			await axios.put(url, updatedDataSelector, { headers: { Authorization: 'Bearer ' + globalData.JWTToken } }).then((response) => {
				console.log(response);
				toast({
					title: 'Selector Updated Successfully',
					description: 'The selector has been updated successfully.',
					status: 'success',
					duration: 5000,
					isClosable: true,
					position: 'top',
				});
				handleClose();
			});
		} catch (error: any) {
			console.error(error);
			toast({
				title: 'Error Updating Selector',
				description: error.response?.data,
				status: 'error',
				duration: 10000,
				isClosable: true,
				position: 'top',
			});
		} finally {
			setShowSubmitButton(true);
		}
	};

	const onPlayfabDataFetch = async () => {
		if (!playfabUserId.trim()) {
			setWarningMessage('Playfab ID cannot be empty.');
			return;
		}

		setWarningMessage('');

		setIsFetching(true);
		let url = config.CLOUDFLARE_LOCAL_ENDPOINT + '/game/' + globalData.gameId + '/playfab_data/' + playfabUserId;
		await axios
			.get(url, { headers: { Authorization: 'Bearer ' + globalData.JWTToken } })
			.then((response) => {
				//const extractedKeys = Object.keys(response.data);
				//setPlayfabUserData(extractedKeys);
				setPlayfabUserData(response.data);
			})
			.finally(() => setIsFetching(false));
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose}>
			<ModalOverlay />
			<ModalContent>
				{selectorInstance ? <ModalHeader>Update Selector</ModalHeader> : <ModalHeader>Create Selector</ModalHeader>}
				<ModalCloseButton />
				<Formik
					initialValues={{
						name: selectorInstance?.name || '',
						description: selectorInstance?.description || '',
						fieldName: selectorInstance?.fieldName || '',
						isJsonQuery: selectorInstance?.isJsonQuery || false,
					}}
					validationSchema={toFormikValidationSchema(dataSelectorSchema)}
					onSubmit={async (values, actions) => {
						if (selectorInstance === undefined) {
							await handleCreateSelector(selectorInstance);
						}

						if (selectorInstance !== null) {
							await handleUpdateSelector(selectorInstance!.id);
						}
						actions.setSubmitting(false);
					}}
				>
					{(props) => (
						<Form>
							<ModalBody>
								<Stack spacing={4}>
									{/* Dropdown below the header */}
									<FormControl mt={4}>
										<FormLabel>Selector Type</FormLabel>
										<Select
											value={selectorType}
											onChange={(e) => {
												const value = e.target.value;
												setSelectorType(value);
												updateDataSelector({
													isDate: value === 'isDate',
													isJsonQuery: value === 'isJsonQuery',
													isSqlQuery: value === 'isSqlQuery',
												});
												props.setFieldValue('isJsonQuery', value === 'isJsonQuery');
											}}
											placeholder="Default Type"
										>
											<option value="isDate">isDate</option>
											<option value="isJsonQuery">isJsonQuery</option>
											{/*<option value="isSqlQuery">isSqlQuery</option>*/}
										</Select>
									</FormControl>
									<Field name="name">
										{({ field, form }: { field: any; form: any }) => (
											<FormControl isInvalid={!!props.errors.name && props.touched.name} isRequired>
												<FormLabel>Name</FormLabel>
												<Input
													{...field}
													value={dataSelector.name}
													onChange={(event) => {
														if (event) {
															form.setFieldValue('name', event.target.value);
															updateDataSelector({ name: event.target.value });
														}
													}}
												/>
												<FormErrorMessage>{props.errors.name}</FormErrorMessage>
											</FormControl>
										)}
									</Field>
									<Field name="description">
										{({ field, form }: { field: any; form: any }) => (
											<FormControl isInvalid={!!props.errors.description && props.touched.description} isRequired>
												<FormLabel>Description</FormLabel>
												<Input
													{...field}
													value={dataSelector.description}
													onChange={(event) => {
														if (event) {
															form.setFieldValue('description', event.target.value);
															updateDataSelector({ description: event.target.value });
														}
													}}
												/>
												<FormErrorMessage>{form.errors.description}</FormErrorMessage>
											</FormControl>
										)}
									</Field>
									{selectorType !== 'isJsonQuery' && selectorType !== 'isSqlQuery' && (
										<>
											<FormControl>
												<FormLabel>Template Playfab Id</FormLabel>
												<HStack>
													<Input
														value={playfabUserId}
														onChange={(event) => setPlayfabUserId(event.target.value)}
														placeholder="Enter Playfab ID"
													/>
													{isFetching ? (
														<Spinner />
													) : (
														<Button onClick={onPlayfabDataFetch} isDisabled={!isAdminOrProduct}>
															Fetch
														</Button>
													)}
												</HStack>
												{warningMessage && (
													<Text color="red.500" fontSize="sm" mt={2}>
														{warningMessage}
													</Text>
												)}
											</FormControl>
											<Field name="fieldNamePlayfabID">
												{({ field, form }: { field: any; form: any }) => (
													<FormControl flex="1" minW={'200px'}>
														<Menu onClose={onCloseModal}>
															<MenuButton as={Button} rightIcon={<ChevronDownIcon />}>
																User Playfab Data
															</MenuButton>
															<MenuList paddingLeft={'auto'} maxHeight="300px" overflowY="auto">
																{renderMenuItems(playfabUserData, '', onCloseModal, (selectedPath) => {
																	form.setFieldValue('fieldName', selectedPath);
																})}
															</MenuList>
														</Menu>
													</FormControl>
												)}
											</Field>
											<Field name="fieldName">
												{({ field, form }: { field: any; form: any }) => (
													<FormControl isInvalid={!!props.errors.fieldName && props.touched.fieldName} isRequired>
														<FormLabel>Selected Field Name</FormLabel>
														<Input
															{...field}
															value={selectedPath}
															onChange={(event) => {
																if (event) {
																	const value = event.target.value;
																	setSelectedPath(value); // Update the state to reflect the typed value
																	form.setFieldValue('fieldName', value); // Update Formik's value
																	updateDataSelector({ fieldName: value });
																}
															}}
														/>
														<FormErrorMessage>{form.errors.fieldName}</FormErrorMessage>
													</FormControl>
												)}
											</Field>
										</>
									)}
									{(selectorType === 'isJsonQuery' || selectorType === 'isSqlQuery') && (
										<>
											<Field name="fieldName">
												{({ field, form }: { field: any; form: any }) => (
													<FormControl isInvalid={!!props.errors.fieldName && props.touched.fieldName} isRequired>
														<FormLabel>Selected Query</FormLabel>
														<Textarea
															{...field}
															value={dataSelector.fieldName}
															onChange={(event) => {
																if (event) {
																	form.setFieldValue('fieldName', event.target.value); // Update Formik's value
																	updateDataSelector({ fieldName: event.target.value });
																}
															}}
														/>
														<FormErrorMessage>{form.errors.fieldName}</FormErrorMessage>
													</FormControl>
												)}
											</Field>{' '}
										</>
									)}
									<HStack justifyContent={'space-between'}>
										<Checkbox
											isDisabled={!isMockCreator}
											isChecked={isMock}
											onChange={() => {
												const newIsMock = !isMock; // Calculate the new value
												setIsMock(newIsMock); // Update local state
												updateDataSelector({ isMock: newIsMock }); // Use the new value
											}}
										>
											Is Mock
										</Checkbox>
										{selectorType === 'isJsonQuery' && (
											<Checkbox
												isChecked={isSticky}
												onChange={() => {
													const newIsSticky = !isSticky;
													setIsSticky(newIsSticky);
													updateDataSelector({ isSticky: newIsSticky });
												}}
											>
												Is Sticky
											</Checkbox>
										)}
									</HStack>
								</Stack>
								<ModalFooter padding={'20px'}>
									<Button variant="outline" mr={3} onClick={onClose}>
										Cancel
									</Button>

									<Button colorScheme="blue" isLoading={props.isSubmitting} isDisabled={!props.isValid || !isAdminOrProduct} type="submit">
										{selectorInstance === undefined ? 'Create' : 'Update'}
									</Button>
								</ModalFooter>
							</ModalBody>
						</Form>
					)}
				</Formik>
			</ModalContent>
		</Modal>
	);
}

export default SelectorModal;
